import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Award,
  CheckCircle2,
  ArrowRight,
  Shield,
  Trophy,
  Target,
  BookOpen,
  Users,
  Clock,
  Star
} from 'lucide-react';

const Certifications = () => {
  const certificationCategories = [
    {
      category: "Cloud Platforms",
      icon: "☁️",
      certifications: [
        {
          provider: "AWS",
          exams: [
            "SAA-C02 (Solutions Architect Associate)",
            "CLF-C01 (Cloud Practitioner)",
            "SAP-C01 (Solutions Architect Professional)",
            "DOP-C01 (DevOps Engineer)",
            "SCS-C01 (Security Specialty)",
            "DVA-C01 (Developer Associate)",
            "SOA-C01 (SysOps Administrator)",
            "All other AWS series"
          ]
        },
        {
          provider: "Azure",
          exams: [
            "AZ-900 (Azure Fundamentals)",
            "AZ-104 (Azure Administrator)",
            "AZ-204 (Azure Developer)",
            "AZ-305 (Azure Solutions Architect)",
            "AZ-400 (DevOps Engineer)",
            "AZ-120 (SAP on Azure)",
            "DP-100 (Data Scientist)",
            "DP-200 (Data Engineer)",
            "DP-201 (Data Engineer)",
            "DA-100 (Data Analyst)",
            "All other Azure series"
          ]
        },
        {
          provider: "Google Cloud",
          exams: [
            "Google ACE (Associate Cloud Engineer)",
            "Google GCD (Cloud Developer)",
            "Google Data Engineer",
            "Google Cloud Architect"
          ]
        }
      ]
    },
    {
      category: "Oil & Gas - Health, Safety & Environment",
      icon: "⚡",
      certifications: [
        {
          provider: "NEBOSH & Safety",
          exams: [
            "NEBOSH IGC (International General Certificate) - Essential for HSE jobs in Middle East",
            "IOSH Managing Safely - Workplace safety management",
            "Fire & Safety Training - Emergency response",
            "H2S Safety / Breathing Apparatus - Critical for oil & gas operations",
            "Confined Space Entry - Safe work in restricted spaces",
            "Work at Height - Fall protection and safety",
            "First Aid / CPR - Emergency medical response",
            "PTW (Permit to Work) - Work authorization systems"
          ]
        },
        {
          provider: "Offshore Safety",
          exams: [
            "BOSIET (Basic Offshore Safety Induction) - Mandatory for offshore jobs",
            "HUET (Helicopter Underwater Escape) - Helicopter safety training",
            "OGUK Medical - Offshore medical fitness certification",
            "Risk Assessment / JSA (Job Safety Analysis)",
            "Emergency Response Team (ERT) Training"
          ]
        }
      ]
    },
    {
      category: "Oil & Gas - Engineering & Technical",
      icon: "🔧",
      certifications: [
        {
          provider: "API - Inspection",
          exams: [
            "API 510 (Pressure Vessel Inspector) - Industry standard for vessel inspection",
            "API 570 (Piping Inspector) - Piping system inspection certification",
            "API 653 (Storage Tank Inspector) - Tank inspection expertise",
            "API 580 (Risk-Based Inspection) - Advanced inspection methodology"
          ]
        },
        {
          provider: "NDT & Welding",
          exams: [
            "NDT Level II / III (RT, UT, MT, PT) - Non-Destructive Testing certification",
            "CSWIP (Welding Inspector) - UK standard welding inspection",
            "AWS CWI (Certified Welding Inspector) - American welding certification",
            "ASME Code Knowledge - Pressure vessel and piping codes"
          ]
        },
        {
          provider: "Electrical & Instrumentation",
          exams: [
            "Hazardous Area Classification - ATEX / IECEx awareness",
            "PLC Programming - Automation systems",
            "SCADA Systems - Supervisory control certification",
            "Instrument Calibration - Precision measurement training"
          ]
        },
        {
          provider: "Field Operations",
          exams: [
            "Rigging & Lifting Supervisor - Heavy lifting operations",
            "Crane Operator Certification - Mobile and tower cranes",
            "Scaffolding Supervisor - Safe scaffold systems",
            "Permit to Work (PTW) - Work authorization"
          ]
        }
      ]
    },
    {
      category: "Project Management",
      icon: "📊",
      certifications: [
        {
          provider: "PMI",
          exams: [
            "PMP (Project Management Professional) - Gold standard for project managers",
            "ACP (Agile Certified Practitioner) - Agile project management",
            "CAPM (Certified Associate) - Entry-level PM certification",
            "PgMP (Program Management) - Multi-project management"
          ]
        },
        {
          provider: "Planning & Control",
          exams: [
            "Primavera P6 - Industry-leading scheduling software",
            "MS Project - Microsoft project planning",
            "Cost Control & Estimation - Project cost management",
            "Planning Engineer Certification"
          ]
        },
        {
          provider: "Peoplecert",
          exams: [
            "ITIL V4 Foundation - India",
            "ITIL V4 Foundation - International",
            "ITIL V4 Managing Professional",
            "Prince 2 Foundation - Structured project method",
            "Prince 2 Practitioner - Advanced project management"
          ]
        },
        {
          provider: "Agile & Scrum",
          exams: [
            "CSM (Certified Scrum Master) - Scrum framework expertise",
            "ACSM (Advanced CSM) - Advanced scrum practices",
            "PSM 1 (Professional Scrum Master) - Scrum.org certification",
            "PSM 2 (Advanced PSM) - Expert-level scrum",
            "SAFe Agilist - Scaled agile framework",
            "SAFe Scrum Master - Enterprise agile"
          ]
        }
      ]
    },
    {
      category: "Security & Compliance",
      icon: "🔒",
      certifications: [
        {
          provider: "ISACA",
          exams: [
            "CISA (Certified Information Systems Auditor) - IT audit and security",
            "CISM (Certified Information Security Manager) - Security governance",
            "CRISC (Risk and Information Systems Control) - Risk management",
            "CGEIT (Governance of Enterprise IT) - IT governance"
          ]
        },
        {
          provider: "CompTIA",
          exams: [
            "Security+ (SYO-601) - Foundation security certification",
            "CompTIA A+ - IT technician fundamentals",
            "CompTIA Network+ - Networking essentials",
            "CAS-003 (Advanced Security) - Enterprise security",
            "PenTest+ - Penetration testing",
            "CySA+ - Cybersecurity analyst"
          ]
        },
        {
          provider: "Security Vendors",
          exams: [
            "CEH (Certified Ethical Hacker) - Offensive security",
            "CyberArk Defender - Privileged access management",
            "Fortinet NSE - Network security expert",
            "Palo Alto PCNSE - Next-gen firewall",
            "Check Point CCSA/CCSE - Security administration",
            "Zscaler - Cloud security platform",
            "SailPoint - Identity governance"
          ]
        },
        {
          provider: "Six Sigma & Quality",
          exams: [
            "Six Sigma Green Belt - Process improvement",
            "Six Sigma Black Belt - Advanced quality management",
            "Lean Six Sigma - Waste reduction methodology",
            "ISO 9001 - Quality management systems"
          ]
        }
      ]
    },
    {
      category: "Development & Integration",
      icon: "💻",
      certifications: [
        {
          provider: "Salesforce",
          exams: [
            "Admin (Administrator) - Platform administration",
            "PD1 (Platform Developer I) - Apex and Visualforce",
            "Advanced Admin - Complex configurations",
            "Sales Cloud Consultant - CRM implementation",
            "Service Cloud Consultant - Customer service solutions"
          ]
        },
        {
          provider: "Mulesoft",
          exams: [
            "MCD1 (Developer Level 1) - Integration basics",
            "MCPA (Platform Architect) - Solution architecture",
            "MCIA (Integration Architect) - Enterprise integration",
            "MCD2 (Developer Level 2) - Advanced development"
          ]
        },
        {
          provider: "Oracle",
          exams: [
            "Oracle Database Administrator - DB management",
            "Oracle Java Certification - Java programming",
            "Oracle Cloud Infrastructure - OCI certification",
            "All Oracle certification codes"
          ]
        },
        {
          provider: "SAP",
          exams: [
            "SAP ERP - Enterprise resource planning",
            "SAP HANA - In-memory database",
            "SAP S/4HANA - Next-gen ERP",
            "SAP MM/SD/FICO modules"
          ]
        }
      ]
    },
    {
      category: "Data & Analytics",
      icon: "📈",
      certifications: [
        {
          provider: "Snowflake",
          exams: [
            "SnowPro Core Certification - Cloud data platform",
            "SnowPro Advanced Architect - Solution design",
            "SnowPro Advanced Administrator - Platform management"
          ]
        },
        {
          provider: "Databricks",
          exams: [
            "Databricks Certified Data Engineer - Data pipeline development",
            "Databricks Data Analyst - Analytics and BI",
            "ML Professional - Machine learning certification"
          ]
        },
        {
          provider: "Tableau & Power BI",
          exams: [
            "Tableau Desktop Specialist - Data visualization",
            "Power BI Data Analyst - Microsoft analytics",
            "Qlik Sense - Business intelligence"
          ]
        }
      ]
    },
    {
      category: "Infrastructure & DevOps",
      icon: "⚙️",
      certifications: [
        {
          provider: "Cisco",
          exams: [
            "CCNA 200-301 - Routing and switching fundamentals",
            "CCNP Enterprise 350-401 - Advanced networking",
            "CCNP Security 350-701 - Network security",
            "CCIE - Expert-level certification"
          ]
        },
        {
          provider: "Linux Foundation",
          exams: [
            "CKA (Certified Kubernetes Administrator) - Container orchestration",
            "CKAD (Kubernetes Application Developer) - K8s development",
            "CKS (Kubernetes Security Specialist) - K8s security",
            "Linux Foundation Certified SysAdmin - Linux administration"
          ]
        },
        {
          provider: "VMware",
          exams: [
            "VCP (VMware Certified Professional) - Virtualization",
            "VCAP (Advanced Professional) - Advanced virtualization",
            "vSphere Administration - Infrastructure management"
          ]
        },
        {
          provider: "DevOps Tools",
          exams: [
            "Terraform - Infrastructure as code",
            "Docker - Containerization platform",
            "Jenkins - CI/CD automation",
            "Ansible - Configuration management"
          ]
        },
        {
          provider: "Nutanix",
          exams: [
            "Nutanix Certified Professional - Hyperconverged infrastructure",
            "Nutanix Certified Advanced Professional"
          ]
        }
      ]
    },
    {
      category: "Automation & Enterprise Platforms",
      icon: "🤖",
      certifications: [
        {
          provider: "RPA & Automation",
          exams: [
            "UiPath - Robotic process automation",
            "Blue Prism - RPA developer",
            "Automation Anywhere - Enterprise automation",
            "Power Automate - Microsoft automation"
          ]
        },
        {
          provider: "ServiceNow",
          exams: [
            "Certified System Administrator - ITSM platform",
            "Certified Implementation Specialist - Service delivery",
            "Certified Application Developer - Custom apps"
          ]
        },
        {
          provider: "Monitoring & Observability",
          exams: [
            "Datadog - Infrastructure monitoring",
            "Splunk - Log analytics",
            "New Relic - Application performance",
            "Dynatrace - Full-stack monitoring"
          ]
        },
        {
          provider: "Low-Code Platforms",
          exams: [
            "Pega - Business process management",
            "OutSystems - Low-code development",
            "Mendix - Rapid application development"
          ]
        }
      ]
    },
    {
      category: "Business Analysis & Testing",
      icon: "🎯",
      certifications: [
        {
          provider: "IIBA",
          exams: [
            "CBAP (Certified Business Analysis Professional) - Expert-level BA",
            "CCBA (Certification of Capability) - Intermediate BA",
            "ECBA (Entry Certificate) - Foundation level",
            "AAC (Agile Analysis) - Agile business analysis"
          ]
        },
        {
          provider: "ISTQB",
          exams: [
            "ISTQB Foundation Level - Software testing basics",
            "ISTQB Advanced Level - Advanced testing techniques",
            "Test Manager - Test management certification",
            "Test Automation Engineer - Automation expertise"
          ]
        }
      ]
    },
    {
      category: "Networking & Infrastructure Vendors",
      icon: "🌐",
      certifications: [
        {
          provider: "Network Vendors",
          exams: [
            "IBM - Networking and cloud",
            "HP (HPE) - Enterprise infrastructure",
            "Huawei - HCIA/HCIP/HCIE certifications",
            "Ruckus - Wireless networking",
            "Nokia - Telecom infrastructure"
          ]
        },
        {
          provider: "Adobe",
          exams: [
            "Adobe Experience Manager - Content management",
            "Adobe Analytics - Digital analytics",
            "Adobe Campaign - Marketing automation"
          ]
        }
      ]
    }
  ];

  const benefits = [
    {
      icon: Trophy,
      title: "100% Pass Guarantee",
      description: "First attempt success guaranteed or money back"
    },
    {
      icon: BookOpen,
      title: "Expert Training",
      description: "Industry certified trainers with real-world experience"
    },
    {
      icon: Clock,
      title: "Flexible Schedule",
      description: "Weekend and evening batches available"
    },
    {
      icon: Users,
      title: "Personalized Support",
      description: "One-on-one mentoring and doubt clearing sessions"
    },
    {
      icon: Target,
      title: "Exam Dumps & Practice",
      description: "Latest exam questions and mock tests"
    },
    {
      icon: Shield,
      title: "Career Support",
      description: "Resume building and interview preparation"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 via-cyan-600 to-blue-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-300 rounded-full blur-3xl animate-pulse delay-700"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <Badge className="bg-white/20 text-white border-white/30 mb-4">
            100% Pass Guarantee
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Professional Certification{' '}
            <span className="text-cyan-200">Exam Support</span>
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8 leading-relaxed">
            Get certified in your first attempt with our guaranteed pass programs. Expert training, 
            latest exam dumps, and personalized support for 100+ certification exams.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg font-semibold shadow-lg">
                Enroll Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+919315649601">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-lg font-semibold">
                Call for Details
              </Button>
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 mt-16 max-w-3xl mx-auto">
            <div>
              <div className="text-4xl font-bold mb-2">100+</div>
              <div className="text-blue-100">Certifications</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">100%</div>
              <div className="text-blue-100">Pass Guarantee</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">5000+</div>
              <div className="text-blue-100">Success Stories</div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Certification Programs?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Industry's best pass guarantee with comprehensive support
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="p-8 text-center hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Available Certification Programs
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Browse through 100+ certification exams with guaranteed first attempt success
            </p>
          </div>

          <div className="space-y-8">
            {certificationCategories.map((category, catIndex) => (
              <Card key={catIndex} className="p-8 border-2 hover:border-blue-300 transition-all duration-300">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="text-4xl">{category.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900">{category.category}</h3>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.certifications.map((cert, certIndex) => (
                    <div key={certIndex} className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-blue-200 transition-all duration-300">
                      <h4 className="text-lg font-bold text-blue-600 mb-4">{cert.provider}</h4>
                      <ul className="space-y-2">
                        {cert.exams.map((exam, examIndex) => (
                          <li key={examIndex} className="flex items-start text-sm">
                            <CheckCircle2 className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{exam}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Success Process
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Choose Certification", desc: "Select from 100+ available certification exams" },
              { step: "02", title: "Expert Training", desc: "Get trained by certified industry professionals" },
              { step: "03", title: "Practice & Prepare", desc: "Access latest dumps and mock exams" },
              { step: "04", title: "Pass First Attempt", desc: "100% guaranteed success in first try" }
            ].map((item, index) => (
              <div key={index} className="relative">
                <Card className="p-6 h-full border-2 hover:border-blue-200 transition-all duration-300">
                  <div className="text-5xl font-bold text-blue-100 mb-4">{item.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </Card>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-blue-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recommended Certifications by Profile */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Recommended Certifications by Profile
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the right certification based on your career path and experience level
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="p-8 bg-white border-2 hover:border-blue-300 transition-all duration-300">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">For Freshers</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">NEBOSH IGC - Safety foundation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">IOSH - Managing safety</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Basic QA/QC course</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">H2S Safety</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">CompTIA A+ / Network+</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8 bg-white border-2 hover:border-blue-300 transition-all duration-300">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <BookOpen className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">For Engineers</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">API 510/570/653 - Inspection</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">QA/QC Certifications</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Primavera P6 - Planning</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">PMP - Project Management</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">AWS/Azure - Cloud platforms</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8 bg-white border-2 hover:border-blue-300 transition-all duration-300">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <Trophy className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">For Supervisors</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">NEBOSH + PTW</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Lifting & Rigging</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Scaffolding Supervisor</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Emergency Response</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8 bg-white border-2 hover:border-blue-300 transition-all duration-300">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-cyan-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">For Offshore Jobs</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">BOSIET - Offshore safety</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">HUET - Helicopter escape</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">H2S Safety</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">OGUK Medical</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8 bg-white border-2 hover:border-blue-300 transition-all duration-300">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Middle East Preferred</h3>
              <p className="text-sm text-gray-600 mb-4">Popular in UAE, Saudi Arabia, Qatar, Oman</p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">NEBOSH IGC</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">API Certifications</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">NDT Level II/III</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">PMP</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">BOSIET</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8 bg-white border-2 hover:border-blue-300 transition-all duration-300">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-6">
                <Star className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">For IT Professionals</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">AWS/Azure/GCP - Cloud</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Kubernetes (CKA/CKAD)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">CISA/CISM - Security</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Salesforce/ServiceNow</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-500">
        <div className="max-w-4xl mx-auto text-center">
          <Award className="w-16 h-16 text-white mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Get Certified?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of successful professionals who passed on their first attempt
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg font-semibold">
                Enroll Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href={`https://wa.me/919315649601?text=I'm interested in certification programs`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-lg font-semibold">
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Certifications;
